import { Auth } from 'aws-amplify';
import { Loader } from 'components';
import { ROUTES } from 'configs/routes';
import { Page404, Page500 } from 'pages';
import { default as AuthRouter } from 'pages/Auth';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import { setHeaders } from 'services';
import { doCheckIsAuthorized } from 'store/actionCreators/auth';
import { getAuthAuthenticated, getAuthLoading } from 'store/selectors/auth';
import { SignUpMethod, trackSignUp } from 'utils/analytics';
import { logInFacebook, logInGoogle } from 'utils/auth';
import {
  captureReturnUrl,
  consumeReturnUrl,
  navigateToPath,
  normalizeAuthUrl,
} from 'utils/authRedirect';

import { FullUser } from '../types';
import { getProfileName } from '../utils';
import AppRouter from './AppRouter';
const Router = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(getAuthAuthenticated);
  const isLoading = useSelector(getAuthLoading);
  // Captured while rendering rather than in an effect: the auth screens read the
  // destination as they render, which happens before any effect runs.
  const [returnUrl] = useState(() => captureReturnUrl());
  const [prevAuth, setPrevAuth] = useLocalStorage<{ [key: string]: string }>(
    'socialSignIns',
    {},
  );

  const onAuthSuccess = (user: FullUser, session) => {
    if (user?.profile && session?.idToken?.payload?.identities) {
      const profileName = getProfileName(user.profile);
      const newIdentities: { [key: string]: string } = {};

      session.idToken.payload.identities.forEach((i) => {
        if (i.providerName === 'Google') {
          newIdentities.google = profileName;
        } else if (i.providerName === 'Facebook') {
          newIdentities.facebook = profileName;
        } else if (i.providerName === 'SignInWithApple') {
          newIdentities.apple = profileName;
        }
      });

      setPrevAuth({ ...prevAuth, ...newIdentities });
    }

    const url = new URL(window.location.href);
    const state = url.searchParams.get('state');

    if (state && user?.profile?.email) {
      try {
        const stateParams = new URLSearchParams(state);
        const flow = stateParams.get('flow');
        const provider = stateParams.get('provider') as SignUpMethod | null;

        if (flow === 'signup' && provider) {
          trackSignUp({
            email: user.profile.email,
            method: provider,
            userId: user.id,
          });
        }
      } catch {
        // ignore malformed state
      }

      url.searchParams.delete('state');
      window.history.replaceState({}, '', url.toString());
    }

    // Social sign-in returns to the fixed Cognito redirect URI, so the deep link
    // the user started from is only available from the captured return URL.
    const returnUrl = consumeReturnUrl();

    if (returnUrl) {
      navigateToPath(returnUrl);
    }
  };

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const error = search.get('error_description');

    if (
      error &&
      /already.found.an.entry.for.username.google/gi.test(error.toString())
    ) {
      logInGoogle();
    } else if (
      error &&
      /already.found.an.entry.for.username.facebook/gi.test(error.toString())
    ) {
      logInFacebook();
    } else if (
      !window.location.pathname.includes('change-email') &&
      !window.location.pathname.includes('verify')
    ) {
      dispatch(
        doCheckIsAuthorized({
          onSuccess: onAuthSuccess,
          // Only once the check has failed do we know the auth UI is really being
          // shown, which is the moment the destination belongs in the URL.
          onFailure: () => normalizeAuthUrl(returnUrl),
        }),
      );
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(refreshSession, 1000 * 60 * 20);

    return () => clearInterval(interval);
  }, []);

  const refreshSession = async () => {
    try {
      const cognitoUser = await Auth.currentAuthenticatedUser();
      const cognitoSession = await Auth.currentSession();

      cognitoUser.refreshSession(
        cognitoSession['refreshToken'],
        (err, session) => {
          if (session) {
            const { idToken } = session;

            setHeaders(idToken.jwtToken);
          }
        },
      );
    } catch (e) {
      console.log('Unable to refresh Token', e);
    }
  };

  return !isLoading ? (
    <BrowserRouter>
      <Routes>
        <Route path={ROUTES.PAGE404} element={<Page404 />} />
        <Route path={ROUTES.PAGE500} element={<Page500 />} />
        {isAuthenticated &&
        !window.location.pathname.includes('change-email') &&
        !window.location.pathname.includes('verify') ? (
          <Route path="*" element={<AppRouter />} />
        ) : (
          <Route path="*" element={<AuthRouter />} />
        )}
      </Routes>
    </BrowserRouter>
  ) : (
    <Loader />
  );
};

export default Router;
